import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBusinessConfig } from '@/hooks/useBusinessConfig';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Sparkles, ArrowLeft } from 'lucide-react';

export const QuoteRequest = () => {
  const navigate = useNavigate();
  const { config } = useBusinessConfig();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    serviceType: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      console.log('Quote Request:', formData);
      
      toast.success('Quote request sent! We\'ll be in touch soon.');
      setFormData({ name: '', email: '', phone: '', serviceType: '', message: '' });
      
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch {
      toast.error('Failed to send request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <Button variant="ghost" onClick={() => navigate(-1)} className="flex items-center gap-2">
        <ArrowLeft className="w-4 h-4" /> Back
      </Button>

      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-900 rounded-2xl mb-4">
          <Sparkles className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-3xl font-black text-slate-900 uppercase tracking-tighter">
          Request a Quote
        </h1>
        <p className="text-slate-600 mt-2">
          Fill out the form below and we'll get back to you within 24 hours
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white p-8 rounded-3xl border-2 border-slate-100 shadow-sm space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Name *</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Your name"
              required
              className="h-12"
            />
          </div>
          <div>
            <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Email *</label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="your@email.com"
              required
              className="h-12"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Phone</label>
            <Input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="0400 000 000"
              className="h-12"
            />
          </div>
          <div>
            <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Service Type *</label>
            <Input
              value={formData.serviceType}
              onChange={(e) => setFormData({ ...formData, serviceType: e.target.value })}
              placeholder="e.g., Full Detail, Roof Clean"
              required
              className="h-12"
            />
          </div>
        </div>

        <div>
          <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Message</label>
          <textarea
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            placeholder="Tell us about your project..."
            className="w-full h-32 rounded-xl border border-slate-200 px-4 py-3 font-medium resize-none focus:border-purple-600 focus:outline-none transition-colors"
          />
        </div>

        <Button
          type="submit"
          disabled={submitting}
          className="w-full h-14 bg-purple-600 hover:bg-purple-700 text-white rounded-2xl font-black uppercase tracking-widest text-sm"
        >
          {submitting ? 'Sending...' : 'Request Quote'}
        </Button>
      </form>

      <div className="text-center text-slate-500 text-sm">
        <p>Or contact us directly:</p>
        <p className="font-bold">{config?.contact_email}</p>
        <p className="font-bold">{config?.contact_phone}</p>
      </div>
    </div>
  );
};
